import React, { useState, useMemo } from 'react';
import UserEditPanel from './UserEditPanel';
import { defaultRoles } from './RoleManagement';
import './UserManagement.css';

export interface User {
    id: string;
    name: string;
    email: string;
    departments: string[];
    roleId: string;
    status: 'active' | 'suspended' | 'pending';
    lastLogin: string;
    requestDate?: string;
}

export const depts = ['본사 (전체)', '서울지점', '경기지점', '강원지점', '충청지점', '경상지점', '전라지점', '제주지점'];
const statuses: ('active' | 'suspended' | 'pending')[] = ['active', 'active', 'pending', 'active', 'active', 'suspended'];

// Mock users
const mockUsers: User[] = Array.from({ length: 42 }, (_, i) => {
    const st = statuses[Math.floor(Math.random() * statuses.length)];
    const isPending = st === 'pending';
    return {
        id: `u${i + 1}`,
        name: ['정지훈', '김태희', '공유', '이동욱', '김고은', '유인나', '육성재', '이병헌', '김태리', '유연석'][i % 10] + (Math.floor(i / 10) || ''),
        email: `manager${i + 1}@eview.app`,
        departments: isPending ? [] : [depts[Math.floor(Math.random() * depts.length)], depts[Math.floor(Math.random() * depts.length)]].filter((v, i, a) => a.indexOf(v) === i).slice(0, i % 4 === 0 ? 2 : 1),
        roleId: isPending ? '' : defaultRoles[i % defaultRoles.length].id,
        status: st,
        lastLogin: isPending ? '-' : `2026-03-${String(Math.max(1, 16 - (i % 5))).padStart(2, '0')} 09:${String(i % 60).padStart(2, '0')}`,
        requestDate: isPending ? `2026-03-${String(Math.max(16, 20 - (i % 3))).padStart(2, '0')} 14:00` : undefined
    };
});

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>(mockUsers);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'suspended'>('all');
    const [filterRole, setFilterRole] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    
    const pageSize = 10;

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchSearch = user.name.includes(searchTerm) || user.email.includes(searchTerm);
            const matchTab = activeTab === 'all' ? user.status !== 'pending' : user.status === activeTab;
            const matchRole = filterRole === 'all' || user.roleId === filterRole;
            return matchSearch && matchTab && matchRole;
        });
    }, [users, searchTerm, activeTab, filterRole]);

    const totalPages = Math.ceil(filteredUsers.length / pageSize);
    const pagedUsers = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredUsers.slice(start, start + pageSize);
    }, [filteredUsers, currentPage]);

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setIsPanelOpen(true);
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setIsPanelOpen(true);
    };

    const handleSaveUser = (updatedUser: any) => {
        if (updatedUser.id) {
            setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
        } else {
            const newUser = { ...updatedUser, id: `u${Date.now()}`, lastLogin: '-', requestDate: '-' };
            setUsers([newUser, ...users]);
        }
        setIsPanelOpen(false);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) setSelectedUserIds(pagedUsers.map(u => u.id));
        else setSelectedUserIds([]);
    };

    const handleSelectUser = (id: string) => {
        if (selectedUserIds.includes(id)) setSelectedUserIds(selectedUserIds.filter(uid => uid !== id));
        else setSelectedUserIds([...selectedUserIds, id]);
    };

    const handleBatchProcess = (action: 'approve' | 'reject' | 'suspend' | 'active' | 'delete') => {
        if (selectedUserIds.length === 0) return;
        let confirmMsg = '';
        if (action === 'approve') confirmMsg = `선택한 ${selectedUserIds.length}명의 회원을 승인하시겠습니까?`;
        if (action === 'reject') confirmMsg = `선택한 ${selectedUserIds.length}명의 요청을 거절하시겠습니까? (요청 정보 지워짐)`;
        if (action === 'suspend') confirmMsg = `선택한 ${selectedUserIds.length}명의 계정을 정지하시겠습니까?`;
        if (action === 'active') confirmMsg = `선택한 ${selectedUserIds.length}명의 정지를 해제하시겠습니까? (다시 정상 활성 상태가 됩니다)`;
        if (action === 'delete') confirmMsg = `정말 선택한 ${selectedUserIds.length}명의 계정을 영구적으로 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`;

        if (window.confirm(confirmMsg)) {
            if (action === 'reject' || action === 'delete') {
                setUsers(users.filter(u => !selectedUserIds.includes(u.id)));
            } else {
                setUsers(users.map(u => {
                    if (selectedUserIds.includes(u.id)) {
                        if (action === 'approve' || action === 'active') return { ...u, status: 'active' };
                        if (action === 'suspend') return { ...u, status: 'suspended' };
                    }
                    return u;
                }));
            }
            setSelectedUserIds([]);
        }
    };

    const getRoleName = (roleId: string) => defaultRoles.find(r => r.id === roleId)?.name || '알 수 없음';
    const pendingCount = users.filter(u => u.status === 'pending').length;

    return (
        <div className="admin-user-mgmt modern-layout" style={{ paddingTop: '0px' }}>
            {/* Quick Tabs Integration & Add Button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid #e2e8f0', marginBottom: '16px' }}>
                <div className="user-mgmt-tabs" style={{ marginBottom: 0, borderBottom: 'none', gap: '8px' }}>
                    <button className={`user-mgmt-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => {setActiveTab('all'); setCurrentPage(1); setSelectedUserIds([]);}}>
                        전체 사용자 <span className="tab-badge">{users.filter(u => u.status !== 'pending').length}</span>
                    </button>
                    <button className={`user-mgmt-tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => {setActiveTab('pending'); setCurrentPage(1); setSelectedUserIds([]);}}>
                        승인 대기 <span className={`tab-badge ${pendingCount > 0 ? 'warn' : ''}`}>{pendingCount}</span>
                    </button>
                    <button className={`user-mgmt-tab ${activeTab === 'suspended' ? 'active' : ''}`} onClick={() => {setActiveTab('suspended'); setCurrentPage(1); setSelectedUserIds([]);}}>
                        이용 정지 <span className="tab-badge">{users.filter(u => u.status === 'suspended').length}</span>
                    </button>
                </div>
            </div>

            <div className={`user-mgmt-table-wrapper data-table-wrapper ${isPanelOpen ? 'panel-open' : ''}`}>
                
                {/* Search & Batch Actions Toolbar */}
                <div className="table-toolbar" style={{ padding: '12px 16px', display: 'flex', background: 'white', borderBottom: '1px solid #e2e8f0', minHeight: '52px', alignItems: 'center' }}>
                    
                    {selectedUserIds.length > 0 ? (
                        <div className="batch-action-bar" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: 0, padding: 0, background: 'transparent', border: 'none' }}>
                            <span className="batch-text" style={{ color: '#0f172a' }}><strong>{selectedUserIds.length}명</strong> 선택됨</span>
                            {activeTab === 'pending' ? (
                                <div className="batch-buttons" style={{ display: 'flex', gap: '8px' }}>
                                    <button className="btn-save-global" onClick={() => handleBatchProcess('approve')}>일괄 승인</button>
                                    <button className="btn-save-global outline" onClick={() => handleBatchProcess('reject')} style={{ color: '#ef4444', borderColor: '#ef4444' }}>승인 거절</button>
                                </div>
                            ) : activeTab === 'suspended' ? (
                                <div className="batch-buttons" style={{ display: 'flex', gap: '8px' }}>
                                    <button className="btn-save-global" onClick={() => handleBatchProcess('active')}>정지 해제</button>
                                    <button className="btn-save-global outline" onClick={() => handleBatchProcess('delete')} style={{ color: '#ef4444', borderColor: '#ef4444' }}>계정 영구 삭제</button>
                                </div>
                            ) : (
                                <div className="batch-buttons" style={{ display: 'flex', gap: '8px' }}>
                                    <button className="btn-save-global outline" onClick={() => handleBatchProcess('suspend')}>계정 정지</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="filters-group" style={{ margin: 0, width: '100%', display: 'flex', gap: '12px' }}>
                            <div className="search-box-wrapper" style={{ margin: 0, width: '260px', flexShrink: 0 }}>
                                <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                                <input 
                                    type="text" 
                                    placeholder="검색" 
                                    className="admin-search-input"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    style={{ width: '100%', paddingLeft: '32px' }}
                                />
                            </div>
                            <select className="filter-select" value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }} style={{ flexShrink: 0 }}>
                                <option value="all">모든 권한 그룹</option>
                                {defaultRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                <table className="user-mgmt-table data-table">
                    <thead>
                        <tr>
                            <th className="th-checkbox" style={{ width: '40px', paddingLeft: '16px' }}>
                                <input type="checkbox" onChange={handleSelectAll} checked={selectedUserIds.length === pagedUsers.length && pagedUsers.length > 0} />
                            </th>
                            <th>개인 정보</th>
                            {activeTab !== 'pending' && activeTab !== 'suspended' && <th>접근 허용 부서</th>}
                            {activeTab !== 'pending' && activeTab !== 'suspended' && <th>적용 권한</th>}
                            {activeTab === 'pending' ? <th>가입 요청일시</th> : (activeTab === 'suspended' ? <th>정지 및 조치일</th> : <th>최근 접속일시</th>)}
                            {activeTab !== 'pending' && <th className="th-action">관리</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {pagedUsers.map(user => (
                            <tr key={user.id} className={editingUser?.id === user.id ? 'active-row' : ''} style={{ background: selectedUserIds.includes(user.id) ? '#f0fdf4' : ''}}>
                                <td style={{ paddingLeft: '16px' }}>
                                    <input type="checkbox" checked={selectedUserIds.includes(user.id)} onChange={() => handleSelectUser(user.id)} />
                                </td>
                                <td>
                                    <div className="user-info-cell">
                                        <div className="avatar-circle">{user.name.charAt(0)}</div>
                                        <div className="user-text-info">
                                            <span className="user-name-bold">{user.name} 
                                                {user.status === 'suspended' && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#ef4444', border: '1px solid #fecaca', background: '#fef2f2', padding: '2px 4px', borderRadius: '4px' }}>정지됨</span>}
                                            </span>
                                            <span className="user-email-dim">{user.email}</span>
                                        </div>
                                    </div>
                                </td>
                                {activeTab !== 'pending' && activeTab !== 'suspended' && (
                                    <>
                                        <td>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '200px' }}>
                                                {user.departments && user.departments.length > 0 
                                                    ? (user.departments.includes('본사 (전체)') 
                                                        ? <span className="dept-tag hq-tag">본사 (전체)</span> 
                                                        : user.departments.map(d => <span key={d} className="dept-tag">{d}</span>))
                                                    : <span style={{ color: '#94a3b8' }}>-</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                                                {user.roleId ? getRoleName(user.roleId) : <span style={{ color: '#ef4444', border: '1px solid #fecaca', background: '#fef2f2', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>권한 미지정</span>}
                                            </span>
                                        </td>
                                    </>
                                )}
                                <td className="date-cell">{activeTab === 'pending' ? user.requestDate : user.lastLogin}</td>
                                {activeTab !== 'pending' && (
                                    <td className="td-action">
                                        <button className="btn-edit-row" onClick={() => handleEditUser(user)}>
                                            {activeTab === 'suspended' ? '계정관리' : '상세보기'}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {filteredUsers.length === 0 && (
                            <tr className="no-data-overlay">
                                <td colSpan={6} className="no-results-cell">
                                    <div className="empty-state">
                                        <p>조건에 맞는 사용자가 없습니다.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* 우측 슬라이딩 패널 */}
                <div className={`side-panel-container ${isPanelOpen ? 'open' : ''}`}>
                    {isPanelOpen && <UserEditPanel user={editingUser} onClose={() => setIsPanelOpen(false)} onSave={handleSaveUser} />}
                </div>
            </div>

            <div className="admin-pagination">
                <div className="pagination-info">
                    전체 <strong>{filteredUsers.length}</strong>명 중 <strong>{filteredUsers.length === 0 ? 0 : (currentPage-1)*pageSize + 1}-{Math.min(filteredUsers.length, currentPage*pageSize)}</strong> 표시
                </div>
                <div className="pagination-controls">
                    <button className="btn-page-arrow" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>&lt;</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button key={page} className={`btn-page-number ${currentPage === page ? 'selected' : ''}`} onClick={() => setCurrentPage(page)}>
                            {page}
                        </button>
                    ))}
                    <button className="btn-page-arrow" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => prev + 1)}>&gt;</button>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
